import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment';

import { Grid, Typography, FormControlLabel, Radio } from '@material-ui/core';
import Loader from 'components/Loader';
import Drawer from 'components/Drawer';
import Button from 'components/Button';
import ButtonConfirmation from 'components/ButtonConfirmation';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/free-solid-svg-icons';

import { loadServicesScheduling, removeServicesScheduling } from 'components/SchedulingServicesRegistration/store/thunk';

import { formatCurrency } from 'helpers/converters';
import { formatPermanence, formatPhone } from 'helpers/formatters';
import useWindowDimensions from 'helpers/hooks/useWindowDimensions';

const Line = ({ children }) => {
  return(
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      {children}
    </div>
  );
}

export default function SchedulingServicesView({ id, isOpen, onClose }) {
  const dispatch = useDispatch();

  const history = useHistory();

  const { width } = useWindowDimensions();
  const isSm = width < 960;

  const { userId } = useSelector(state => state.profile);
  const { establishmentId } = useSelector(state => state.businessInfo);
  const { isLoading, submit } = useSelector(state => state.schedulingServices);

  const [actionEventType, setActionEventType] = useState('all');

  useEffect(() => {
    if(id) {
      dispatch(loadServicesScheduling(userId, establishmentId, id));
    }
  }, [id]);

  const getServicesTotal = (discount, services) => {
    const { amount, type } = discount || {};

    const servicesAmount = services?.reduce((total, service) => total + (+service.amount * +service.quantity), 0);

    if(+type == 1) {
      return formatCurrency(servicesAmount - (servicesAmount * amount / 100));
    }

    if(+type == 2) {
      return formatCurrency(servicesAmount - amount);
    }

    return formatCurrency(servicesAmount);
  }

  return(
    <Drawer
      id='scheduling-services-drawer'
      anchor='left'
      title='Detalhes do agendamento'
      open={isOpen}
      onClose={onClose}
    >
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
        <Loader isLoading={isLoading}>
          <Grid container>
            <Grid xs={12}>
              <Typography color='primary' style={{ borderBottom: '1px solid #E4E7EA', marginBottom: 10 }}>
                <b>Serviços</b>
              </Typography>
              {submit?.services?.map(service => {
                return(
                  <Line>
                    <Typography variant='subtitle2'>{service?.quantity}x {service?.serviceName}</Typography>
                    <Typography variant='subtitle2' color='textSecondary'>{formatCurrency(service?.amount * service?.quantity)}</Typography>
                  </Line>
                );
              })}
              {(!!submit?.discount?.amount && +submit?.discount?.amount > 0) && (
                <Line>
                  <Typography variant='subtitle2'>Desconto</Typography>
                  {(+submit?.discount?.type == 1) && (
                    <Typography variant='subtitle2' color='textSecondary'>
                      {parseInt(submit?.discount?.amount)}%
                    </Typography>
                  )}
                  {(+submit?.discount?.type == 2) && (
                    <Typography variant='subtitle2' color='textSecondary'>
                      {formatCurrency(submit?.discount?.amount)}
                    </Typography>
                  )}
                </Line>
              )}
              <Line>
                <Typography variant='subtitle2'>TOTAL</Typography>
                <Typography variant='subtitle2' color='textSecondary'>
                  {getServicesTotal(submit?.discount, submit?.services)}
                </Typography>
              </Line>
              {(submit?.services?.reduce((total, service) => total + +service.executionTime, 0) > 0) && (
                <Line>
                  <Typography variant='subtitle2'>Tempo de execução</Typography>
                  <Typography variant='subtitle2' color='textSecondary'>
                    {moment.utc().startOf('day').add({ minutes: submit?.services?.reduce((total, service) => total + +service.executionTime, 0) }).format('HH[h] mm[min]')}
                  </Typography>
                </Line>
              )}
            </Grid>
            <Grid xs={12}>
              <Typography color='primary' style={{ borderBottom: '1px solid #E4E7EA', marginTop: 10, marginBottom: 10 }}>
                <b>Data e Hora</b>
              </Typography>
              <Line>
                <Typography variant='subtitle2'>Início</Typography>
                <Typography variant='subtitle2' color='textSecondary'>
                  {moment(submit?.dateStart, 'YYYY-MM-DD').format('DD/MM/YYYY')} - {submit?.timeStart}
                </Typography>
              </Line>
              <Line>
                <Typography variant='subtitle2'>Fim</Typography>
                <Typography variant='subtitle2' color='textSecondary'>
                  {moment(submit?.dateEnd, 'YYYY-MM-DD').format('DD/MM/YYYY')} - {submit?.timeEnd}
                </Typography>
              </Line>
              <Line>
                <Typography variant='subtitle2'>Período</Typography>
                <Typography variant='subtitle2' color='textSecondary'>
                  {formatPermanence([`${submit?.dateEnd} ${submit?.timeEnd}`, `${submit?.dateStart} ${submit?.timeStart}`])}
                </Typography>
              </Line>
            </Grid>
            <Grid xs={12}>
              <Typography color='primary' style={{ borderBottom: '1px solid #E4E7EA', marginTop: 10, marginBottom: 10 }}>
                <b>Informações do cliente</b>
              </Typography>
              <Line>
                <Typography variant='subtitle2'>Cliente</Typography>
                <Typography variant='subtitle2' color='textSecondary'>{submit?.client?.name || '-'}</Typography>
              </Line>
              <Line>
                <Typography variant='subtitle2'>Telefone</Typography>
                <Typography variant='subtitle2' color='textSecondary'>{!!submit?.client?.phone ? formatPhone(submit?.client?.phone) : '-'}</Typography>
              </Line>
              <Line>
                <Typography variant='subtitle2'>E-mail</Typography>
                <Typography variant='subtitle2' color='textSecondary'>{submit?.client?.email || '-'}</Typography>
              </Line>
            </Grid>
            <Grid xs={12}>
              <Typography color='primary' style={{ borderBottom: '1px solid #E4E7EA', marginTop: 10, marginBottom: 10 }}>
                <b>Informações do veículo</b>
              </Typography>
              <Line>
                <Typography variant='subtitle2'>Placa</Typography>
                <Typography variant='subtitle2' color='textSecondary'>{submit?.vehicle?.plate || '-'}</Typography>
              </Line>
              <Line>
                <Typography variant='subtitle2'>Modelo</Typography>
                <Typography variant='subtitle2' color='textSecondary'>{submit?.vehicle?.vehicleModel || '-'}</Typography>
              </Line>
              <Line>
                <Typography variant='subtitle2'>Cor</Typography>
                <Typography variant='subtitle2' color='textSecondary'>{submit?.vehicle?.vehicleColor || '-'}</Typography>
              </Line>
            </Grid>
          </Grid>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <div style={{ width: isSm ? '50%' : 'fit-content' }}>
              {!!submit?.recurrenceId && (
                <ButtonConfirmation
                  fullWidth={isSm}
                  title='Cancelar Agendamento'
                  color='error'
                  variant='outlined'
                  isCancelButton={false}
                  modalTitle='Cancelar agendamento'
                  modalText={
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <FormControlLabel
                        style={{ backgroundColor: (actionEventType == 'single') ? '#F0F2F6' : '#FCFCFC', border: '1px solid #D7D7D7', borderRadius: 5, margin: 0 }}
                        control={
                          <Radio
                            style={{ color: '#1E5168' }}
                            checked={actionEventType == 'single'}
                            onChange={() => setActionEventType('single')}
                          />
                        }
                        label={
                          <Typography color='textSecondary' variant='body2'>
                            Este evento
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        style={{ backgroundColor: (actionEventType == 'all') ? '#F0F2F6' : '#FCFCFC', border: '1px solid #D7D7D7', borderRadius: 5, margin: 0 }}
                        control={
                          <Radio
                            style={{ color: '#1E5168' }}
                            checked={actionEventType == 'all'}
                            onChange={() => setActionEventType('all')}
                          />
                        }
                        label={
                          <Typography color='textSecondary' variant='body2'>
                            Este e os seguintes eventos
                          </Typography>
                        }
                      />
                    </div>
                  }
                  modalButtons={[
                    {
                      fullWidth: true,
                      color: 'error',
                      title: 'Cancelar agendamento',
                      onClick: () => {
                        const params = {
                          recurrence: {
                            applyToAll: actionEventType == 'all'
                          }
                        }

                        dispatch(removeServicesScheduling(userId, establishmentId, id, params))
                          .then(() => onClose());
                      }
                    }
                  ]}
                />
              )}
              {!submit?.recurrenceId && (
                <ButtonConfirmation
                  fullWidth={isSm}
                  title='Cancelar Agendamento'
                  color='error'
                  variant='outlined'
                  cancelTitle='Fechar'
                  cancelColor='tertiary'
                  cancelVariant='outlined'
                  cancelFullWidth={isSm}
                  modalTitle='Confimar cancelamento'
                  modalText={
                    <>
                      <Typography color='textSecondary' variant='subtitle2'>
                        {isSm ? 'Deseja confimar o cancelamento do agendamento?' : 'Deseja confimar o cancelamento do agendamento:'}
                      </Typography>
                      {!isSm && (
                        <Typography color='primary' variant='subtitle2'>
                          {(submit?.services?.length > 0) && submit?.services[0]?.serviceName} ({moment(submit?.timeStart, 'HH:mm').format('HH:mma')} - {moment(submit?.timeEnd, 'HH:mm').format('HH:mma')})
                        </Typography>
                      )}
                      {isSm && (
                        <>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #E4E7EA', marginTop: 10, marginBottom: 10 }}>
                            <Typography variant='subtitle2'>Cliente</Typography>
                            <Typography variant='subtitle2' color='textSecondary'>{submit?.clientName || '-'}</Typography>
                          </div>
                          <Typography color='primary' style={{ borderBottom: '1px solid #E4E7EA', marginBottom: 10 }}>
                            <b>Serviços</b>
                          </Typography>
                          {submit?.services?.map(service => {
                            return(
                              <Line>
                                <Typography variant='subtitle2'>{service.quantity}x {service.serviceName}</Typography>
                                <Typography variant='subtitle2' color='textSecondary'>{formatCurrency(service.amount)}</Typography>
                              </Line>
                            );
                          })}
                          <Typography color='primary' style={{ borderBottom: '1px solid #E4E7EA', marginTop: 10, marginBottom: 10 }}>
                            <b>Data e Hora</b>
                          </Typography>
                          <Line>
                            <Typography variant='subtitle2'>Início</Typography>
                            <Typography variant='subtitle2' color='textSecondary'>
                              {moment(submit?.dateStart, 'YYYY-MM-DD').format('DD/MM/YYYY')} - {submit?.timeStart}
                            </Typography>
                          </Line>
                          <Line>
                            <Typography variant='subtitle2'>Fim</Typography>
                            <Typography variant='subtitle2' color='textSecondary'>
                              {moment(submit?.dateEnd, 'YYYY-MM-DD').format('DD/MM/YYYY')} - {submit?.timeEnd}
                            </Typography>
                          </Line>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #E4E7EA', marginTop: 20, marginBottom: 10 }}>
                            <Typography variant='subtitle2'>TOTAL</Typography>
                            <Typography variant='subtitle2' color='textSecondary'>
                              {formatCurrency(submit?.services?.reduce((total, service) => total + +service.amount, 0))}
                            </Typography>
                          </div>
                        </>
                      )}
                    </>
                  }
                  modalButtons={[
                    {
                      fullWidth: isSm,
                      color: 'error',
                      title: 'Cancelar agendamento',
                      onClick: () => {
                        dispatch(removeServicesScheduling(userId, establishmentId, id))
                          .then(() => onClose());
                      }
                    }
                  ]}
                />
              )}
            </div>
            <div style={{ width: isSm ? '50%' : 'fit-content' }}>
              <Button
                fullWidth={isSm}
                type='button'
                color='tertiary'
                variant='outlined'
                onClick={() => {
                  history.push({
                    pathname: '/editar-agendamento-servico',
                    state: {
                      data: {
                        id
                      }
                    }
                  });
                }}
                startIcon={
                  <FontAwesomeIcon
                    size='xs'
                    icon={faPen}
                  />
                }
              >
                Editar
              </Button>
            </div>
          </div>
        </Loader>
      </div>
    </Drawer>
  );
}